import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Checkbox,
  SearchInput,
  Badge,
  ScrollArea,
  toast,
} from '@aaas/shared';
import { Layers, Loader2, X } from 'lucide-react';
import { usePoolListQuery } from '../hooks/usePoolQueries';
import { BULK_POOL_LIMIT } from '../types';

const POOL_LIMIT = BULK_POOL_LIMIT;

interface AddCVToPoolDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (poolIds: string[]) => Promise<void>;
  workspaceId: string | undefined;
  idToken: string | undefined;
  existingPoolIds?: string[];
  title?: string;
}

export function AddCVToPoolDialog({
  open,
  onClose,
  onConfirm,
  workspaceId,
  idToken,
  existingPoolIds = [],
  title,
}: AddCVToPoolDialogProps) {
  const { t } = useTranslation('ats');
  const [selectedPoolIds, setSelectedPoolIds] = useState<Set<string>>(new Set());
  const [selectedPoolNames, setSelectedPoolNames] = useState<Map<string, string>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: poolsData, isLoading: poolsLoading } = usePoolListQuery({
    workspaceId,
    idToken,
    size: 100,
    search: searchQuery || undefined,
    enabled: open && !!workspaceId && !!idToken,
    refetchInterval: false,
  });

  const existingSet = useMemo(() => new Set(existingPoolIds), [existingPoolIds]);
  const allPools = useMemo(
    () => (poolsData?.data?.items || []).filter((p) => !existingSet.has(p.id)),
    [poolsData, existingSet]
  );

  useEffect(() => {
    if (!open) {
      setSelectedPoolIds(new Set());
      setSelectedPoolNames(new Map());
      setSearchQuery('');
      setError(null);
    }
  }, [open]);

  const handleTogglePool = useCallback((poolId: string, poolName: string) => {
    setSelectedPoolIds((prev) => {
      const next = new Set(prev);
      if (next.has(poolId)) {
        next.delete(poolId);
      } else {
        if (next.size >= POOL_LIMIT) return prev;
        next.add(poolId);
      }
      return next;
    });
    setSelectedPoolNames((prev) => {
      const next = new Map(prev);
      if (next.has(poolId)) {
        next.delete(poolId);
      } else {
        if (next.size >= POOL_LIMIT) return prev;
        next.set(poolId, poolName);
      }
      return next;
    });
  }, []);

  const handleRemoveSelected = useCallback((poolId: string) => {
    setSelectedPoolIds((prev) => {
      const next = new Set(prev);
      next.delete(poolId);
      return next;
    });
    setSelectedPoolNames((prev) => {
      const next = new Map(prev);
      next.delete(poolId);
      return next;
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (selectedPoolIds.size === 0) {
      setError(t('addCvToPool.selectAtLeastOne'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm(Array.from(selectedPoolIds));
      toast.success(t('addCvToPool.toast.success', { count: selectedPoolIds.size }));
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('addCvToPool.addFailed');
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedPoolIds, onConfirm, onClose, t]);

  const limitReached = selectedPoolIds.size >= POOL_LIMIT;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-500" />
            {title || t('addCvToPool.defaultTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Selected pools summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-900 mb-2">
              {t('addCvToPool.selected', { count: selectedPoolIds.size, limit: POOL_LIMIT })}
            </p>
            <div className="h-8">
              <ScrollArea className="h-8">
                {selectedPoolIds.size > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {Array.from(selectedPoolNames.entries()).map(([id, name]) => (
                      <Badge
                        key={id}
                        variant="secondary"
                        className="bg-blue-100 text-blue-900 hover:bg-blue-200 pr-1 py-1"
                      >
                        <span className="mr-1">{name}</span>
                        <button
                          onClick={() => handleRemoveSelected(id)}
                          className="inline-flex items-center justify-center rounded-full hover:bg-blue-300 p-0.5"
                          aria-label={t('addCvToPool.removeSelectedAria', { name })}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 w-full h-full flex items-center justify-center">
                    {t('addCvToPool.noPoolsSelected')}
                  </p>
                )}
              </ScrollArea>
            </div>
          </div>

          {/* Pool selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('addCvToPool.pools')}</Label>
              {limitReached && (
                <p className="text-xs text-amber-600">{t('addCvToPool.limitReached', { limit: POOL_LIMIT })}</p>
              )}
            </div>

            <SearchInput
              placeholder={t('addCvToPool.searchPlaceholder')}
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              width="xl"
              className="w-full"
              showClearButton
            />

            {poolsLoading ? (
              <div className="flex items-center justify-center py-8 h-56 border border-gray-200 rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : allPools.length > 0 ? (
              <div className="border border-gray-200 rounded-lg">
                <ScrollArea className="h-56">
                  {allPools.map((pool) => {
                    const isSelected = selectedPoolIds.has(pool.id);
                    const isDisabled = limitReached && !isSelected;
                    return (
                      <label
                        key={pool.id}
                        className={`flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 ${
                          isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          onCheckedChange={() => handleTogglePool(pool.id, pool.name)}
                        />
                        <span className="text-sm text-gray-900">{pool.name}</span>
                      </label>
                    );
                  })}
                </ScrollArea>
              </div>
            ) : searchQuery ? (
              <div className="flex items-center justify-center h-56 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-500">
                  {t('addCvToPool.noPoolsMatching', { query: searchQuery })}
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center h-56 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-500">
                  {existingPoolIds.length > 0 ? t('addCvToPool.alreadyInAll') : t('addCvToPool.noPoolsAvailable')}
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('addCvToPool.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={selectedPoolIds.size === 0 || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('addCvToPool.adding')}
              </>
            ) : (
              t('addCvToPool.addToPools', { count: selectedPoolIds.size })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
